// src/dashboard/pages/teacher/ToDoList/ToDoListDashboard.jsx

import React, { useMemo } from "react";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import useScrollToTop from "../../../../hooks/useScollToTop";
import { useUser } from "../../../../pages/Home/UserContext";
import { getUserRoleId } from "../../../config/dashboardMenuConfig";

import { useToDoListDashboard } from "./hooks/useToDoListDashboard";

import DashboardToDoListHeader from "./components/DashboardToDoListHeader";
import ToDoFiltersCard from "./components/ToDoFiltersCard";
import ToDoKpiCards from "./components/ToDoKpiCards";
import TaskKanbanBoard from "./components/TaskKanbanBoard";
import TaskEmptyState from "./components/TaskEmptyState";
import AddTaskDrawer from "./components/AddTaskDrawer";
import EditTaskDrawer from "./components/EditTaskDrawer";

function getStoredUserInfo() {
  try {
    const raw = localStorage.getItem("userInfo");
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function UnauthorizedDashboardPanel() {
  return (
    <section className="w-full pb-10 pt-5 font-satoshi">
      <div className="mx-auto max-w-[1900px] px-3 sm:px-4 lg:px-6">
        <div className="overflow-hidden rounded-[28px] border border-rose-200 bg-white shadow-sm dark:border-rose-500/20 dark:bg-slate-900">
          <div className="h-1.5 bg-gradient-to-r from-rose-500 to-orange-500" />

          <div className="p-5 sm:p-6">
            <p className="text-[11px] uppercase tracking-[0.22em] text-rose-500">
              Accès refusé
            </p>

            <h1 className="mt-1 text-2xl font-bold text-slate-900 dark:text-white">
              To-Do List
            </h1>

            <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600 dark:text-slate-300">
              Cette page est réservée aux enseignants et administrateurs du
              club.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function ToDoListDashboard() {
  useScrollToTop();

  const { user } = useUser();
  const storedUser = useMemo(() => getStoredUserInfo(), []);
  const currentUser = user || storedUser;
  const roleId = getUserRoleId(currentUser);

  const {
    tasks,
    filteredTasks,
    groupedTasks,
    users,
    categoryOptions,
    stats,

    filters,
    setFilters,

    loading,
    saving,
    deletingId,
    dragOverStatus,

    addDrawerOpen,
    editDrawerOpen,
    currentTask,

    openAddDrawer,
    closeAddDrawer,
    openEditDrawer,
    closeEditDrawer,

    handleCreateTask,
    handleUpdateTask,
    handleDeleteTask,
    handleChangeTaskStatus,

    handleDragStart,
    handleDragOver,
    handleDragEnter,
    handleDragLeave,
    handleDrop,

    refreshTasks,
  } = useToDoListDashboard(currentUser);

  if (![1, 3].includes(roleId)) {
    return <UnauthorizedDashboardPanel />;
  }

  return (
    <section className="w-full pb-10 pt-5 font-satoshi">
      <div className="mx-auto max-w-[1900px] space-y-5 px-3 sm:px-4 lg:px-6">
        <DashboardToDoListHeader />

        <ToDoFiltersCard
          filters={filters}
          setFilters={setFilters}
          users={users}
          categoryOptions={categoryOptions}
          loading={loading}
          onAdd={openAddDrawer}
          onRefresh={refreshTasks}
        />

        <ToDoKpiCards stats={stats} loading={loading} />

        <section className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
          <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

          <div className="p-4 sm:p-5 lg:p-6">
            <div className="flex flex-col gap-2 lg:flex-row lg:items-end lg:justify-between">
              <div>
                <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
                  Kanban des tâches
                </p>

                <h2 className="mt-1 text-xl font-bold text-slate-900 dark:text-white">
                  Suivi des tâches enseignants
                </h2>

                <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600 dark:text-slate-300">
                  Déplacez les cartes entre colonnes pour changer leur statut,
                  ou utilisez le sélecteur directement dans chaque carte.
                </p>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm dark:border-slate-700 dark:bg-slate-800/60">
                <span className="text-slate-500 dark:text-slate-400">
                  Tâches affichées :
                </span>{" "}
                <span className="font-bold text-slate-900 dark:text-white">
                  {filteredTasks.length}
                </span>
                <span className="text-slate-500 dark:text-slate-400">
                  {" "}
                  / {tasks.length}
                </span>
              </div>
            </div>

            {loading ? (
              <div className="mt-5 grid min-w-[980px] grid-cols-4 gap-4 overflow-x-auto">
                {[...Array(4)].map((_, columnIndex) => (
                  <div
                    key={columnIndex}
                    className="min-h-[420px] animate-pulse rounded-2xl border border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-800/60"
                  />
                ))}
              </div>
            ) : filteredTasks.length === 0 ? (
              <TaskEmptyState onAdd={openAddDrawer} />
            ) : (
              <TaskKanbanBoard
                groupedTasks={groupedTasks}
                dragOverStatus={dragOverStatus}
                deletingId={deletingId}
                onEdit={openEditDrawer}
                onDelete={handleDeleteTask}
                onChangeStatus={handleChangeTaskStatus}
                onDragStart={handleDragStart}
                onDragOver={handleDragOver}
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              />
            )}
          </div>
        </section>
      </div>

      <AddTaskDrawer
        isOpen={addDrawerOpen}
        onClose={closeAddDrawer}
        onSubmit={handleCreateTask}
        saving={saving}
        users={users}
        categoryOptions={categoryOptions}
      />

      <EditTaskDrawer
        isOpen={editDrawerOpen}
        onClose={closeEditDrawer}
        task={currentTask}
        onSubmit={handleUpdateTask}
        onDelete={handleDeleteTask}
        saving={saving}
        deletingId={deletingId}
        users={users}
        categoryOptions={categoryOptions}
      />

      <ToastContainer />
    </section>
  );
}
